import { nextTick } from "vue";

import { CartControllerMode, ServiceType } from "@/data/model/Enums";
import type { IService } from "@/data/model/Service";

import { appEvents, features } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { AllocationController } from "@/app/controller/AllocationController";
import { AppRateController } from "@/app/controller/AppRateController";
import { BaseController, ViewTransition } from "@/app/controller/BaseController";
import { BillController } from "@/app/controller/BillController";
import { CartController } from "@/app/controller/CartController";
import { CartController_New } from "@/app/controller/CartController_new";
import { CovidController } from "@/app/controller/CovidController";
import { CreditCardController } from "@/app/controller/CreditCardController";
import { DocumentsController } from "@/app/controller/DocumentsController";
import { DoneController, type IDoneControllerTransitionData } from "@/app/controller/DoneController";
import { HelpController } from "@/app/controller/HelpController";
import { OpenBillsController } from "@/app/controller/OpenBillsController";
import { OrderController } from "@/app/controller/OrderController";
import { OrderHistoryController } from "@/app/controller/OrderHistoryController";
import { PayController } from "@/app/controller/PayController";
import { PaymentHistoryController } from "@/app/controller/PaymentHistoryController";
import { PaymentMethodReportController } from "@/app/controller/PaymentMethodReportController";
import {
    type IPaymentReceiptControllerTransitionData,
    PaymentReceiptController,
} from "@/app/controller/PaymentReceiptController";
import { PreorderTimeController } from "@/app/controller/PreorderTimeController";
import { ProfileController } from "@/app/controller/ProfileController";
import { type IReceiptControllerTransitionData, ReceiptController } from "@/app/controller/ReceiptController";
import { ReportIssueController } from "@/app/controller/ReportIssueController";
import { RoomPlanController } from "@/app/controller/RoomPlanController";
import { ServicesController } from "@/app/controller/ServicesController";
import { Helper } from "@/app/ui/Helper";

export class ControllerManager {
    public allocationController?: AllocationController;
    private covidController?: CovidController;
    private servicesController?: ServicesController;

    public orderController?: OrderController;
    private preorderTimeController?: PreorderTimeController;
    private cartController?: CartController | CartController_New;
    private payController?: PayController;
    private billController?: BillController;

    private documentsController?: DocumentsController;

    public doneController?: DoneController;
    private profileController?: ProfileController;
    private orderHistoryController?: OrderHistoryController;
    private receiptController?: ReceiptController;
    private creditCardController?: CreditCardController;
    private paymentHistoryController?: PaymentHistoryController;
    private paymentReceiptController?: PaymentReceiptController;
    private openBillsController?: OpenBillsController;
    private roomPlanController?: RoomPlanController;
    private paymentMethodReportController?: PaymentMethodReportController;

    private helpController?: HelpController;
    private reportIssueController?: ReportIssueController;
    private appRateController?: AppRateController;

    private $mainContainer!: JQuery;

    private lastActiveRootView?: BaseController;

    public onServiceClicked?: (newService: IService) => Promise<void>;

    constructor($mainContainer: JQuery) {
        this.$mainContainer = $mainContainer;

        appEvents.OnShowCreditCards.add(() => this.showCreditCards());
        appEvents.OnShowOrderHistory.add(() => this.showOrderHistory());
        appEvents.OnShowPaymentHistory.add(() => this.showPaymentHistory());
        appEvents.OnShowProfile.add(() => this.showProfile());
        appEvents.OnShowHelp.add(() => this.showHelp());
        appEvents.OnShowOpenBills.add(() => this.showOpenBills());
        appEvents.OnShowPaymentMethodReport.add(() => this.showPaymentMethodsReport());
        appEvents.OnShowReceipt.add((data) => this.showReceipt(data));
        appEvents.OnShowPaymentReceipt.add((data) => this.showPaymentReceipt(data));
    }

    public initializeBasicControllers() {
        //do async for performance increase
        this.allocationController = new AllocationController();
        this.allocationController.stackTo(this.$mainContainer);

        this.servicesController = new ServicesController();
        this.servicesController.stackTo(this.$mainContainer);

        if (Helper.hasService(ServiceType.Order)) {
            this.orderController = new OrderController();
            this.orderController.stackTo(this.$mainContainer);
        }

        if (Helper.hasService(ServiceType.Covid)) {
            this.covidController = new CovidController();
            this.covidController.stackTo(this.$mainContainer);
        }
    }

    public showPayOrder(transition?: ViewTransition) {
        if (this.billController === undefined) {
            this.billController = new BillController();
            this.billController.stackTo(this.$mainContainer);
        }

        this.billController.onContinue = () => this.showPay(true, ViewTransition.SlideLeft);
        this.billController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.billController.show(transition);
    }

    public async showOrder(options: {
        isMainNavigation: boolean;
        hasPreorderTimeStep?: boolean;
        transition?: ViewTransition;
    }) {
        if (this.orderController === undefined) {
            this.orderController = new OrderController();
            this.orderController.stackTo(this.$mainContainer);
            await nextTick();
        } else {
            if (options.hasPreorderTimeStep === true) {
                this.orderController.reset();
            }
        }

        this.orderController.onContinue = () => this.showCart(ViewTransition.SlideLeft);
        // if is main navigation -> starting from main screen
        if (options.isMainNavigation) {
            // if is preorder service - service starts with preorder screen
            if (options.hasPreorderTimeStep) {
                this.orderController.onGoBack = () =>
                    this.showPreorder({ isMainNavigation: true }, ViewTransition.SlideRight);
            }
            // otherwise starts from main screen
            else {
                this.orderController.onShowServices = () => this.showServices(ViewTransition.Fade);
            }
        }

        this.orderController.show(options.transition, {
            isMainNavigation: options.isMainNavigation,
            hasPreorderTimeStep: options.hasPreorderTimeStep,
        });
    }

    public showPreorder(options: { isMainNavigation: boolean }, transition?: ViewTransition) {
        if (this.preorderTimeController === undefined) {
            this.preorderTimeController = new PreorderTimeController();
            this.preorderTimeController.stackTo(this.$mainContainer);
        }

        this.preorderTimeController.onContinue = () => {
            this.showOrder({
                isMainNavigation: true,
                hasPreorderTimeStep: true,
                transition: ViewTransition.SlideLeft,
            });
        };
        this.preorderTimeController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.preorderTimeController.show(transition, options);
    }

    public showCovid(transition?: ViewTransition) {
        if (this.covidController === undefined) {
            this.covidController = new CovidController();
            this.covidController.stackTo(this.$mainContainer);
        }

        this.covidController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.covidController.show(transition);
    }

    public showDocuments(transition?: ViewTransition) {
        if (this.documentsController === undefined) {
            this.documentsController = new DocumentsController();
            this.documentsController.stackTo(this.$mainContainer);
        }

        this.documentsController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.documentsController.show(transition);
    }

    private showCart(transition?: ViewTransition) {
        const service = Helper.getCurrentOrderOrOfferService();
        const cartMode =
            service!.serviceType === ServiceType.Offer ? CartControllerMode.Offer : CartControllerMode.Order;

        if (this.cartController === undefined) {
            this.cartController = features.vueCartController
                ? new CartController_New(cartMode)
                : new CartController(cartMode);
            this.cartController.stackTo(this.$mainContainer);
        }

        this.cartController.updateMode(cartMode);
        this.cartController.onGoBack = () => {
            this.showOrder({
                isMainNavigation: false,
                transition: ViewTransition.SlideRight,
            });
        };
        this.cartController.onContinue = () => this.showPay(false, ViewTransition.SlideLeft);
        this.cartController.show(transition);
    }

    private showPay(isForPayService: boolean, transition?: ViewTransition) {
        if (this.payController === undefined) {
            this.payController = new PayController();
            //this.payController = new PayController_new();
            this.payController.stackTo(this.$mainContainer);
        }

        if (isForPayService) {
            this.payController.onGoBack = () => {
                TastierClient.context.reloadBill();
                this.showPayOrder(ViewTransition.SlideRight);

                if (this.payController?.shouldShowSelectionsChangedPopupOnLeave) {
                    this.billController?.showSelectionChangedPopup();
                }
            };
        } else {
            this.payController.onGoBack = () => this.showCart(ViewTransition.SlideRight);
        }

        this.payController.show(transition, {
            isMainNavigation: true,
            isForPayService: isForPayService,
        });
    }

    public showDone(data: IDoneControllerTransitionData, transition?: ViewTransition, onShowPopover?: { () }) {
        if (this.doneController === undefined) {
            this.doneController = new DoneController();
            this.doneController.stackTo(this.$mainContainer);
        }

        this.doneController.onPopoverBecameVisible = onShowPopover;

        this.doneController.onStartOver = () => {
            this.goToFirstViewController(ViewTransition.Fade);
        };

        this.doneController.show(transition, data);
    }

    private showProfile() {
        if (this.profileController === undefined) {
            this.profileController = new ProfileController();
            this.profileController.stackTo(this.$mainContainer);
        }

        this.lastActiveRootView = this.getActiveViewController();
        this.profileController.onGoBack = () => {
            this.goBackToLastActiveRootView();
        };
        this.profileController.show(ViewTransition.SlideLeft);
    }

    private showCreditCards() {
        if (this.creditCardController === undefined) {
            this.creditCardController = new CreditCardController();
            this.creditCardController.stackTo(this.$mainContainer);
        }

        this.lastActiveRootView = this.getActiveViewController();
        this.creditCardController.onGoBack = () => {
            this.goBackToLastActiveRootView();
        };

        this.creditCardController.show(ViewTransition.SlideLeft);
    }

    private showOrderHistory(transition?: ViewTransition) {
        if (this.orderHistoryController === undefined) {
            this.orderHistoryController = new OrderHistoryController();
            this.orderHistoryController.stackTo(this.$mainContainer);
        }

        if (transition === undefined) {
            this.lastActiveRootView = this.getActiveViewController();
        }

        this.orderHistoryController.onClick = (m) =>
            this.showReceipt({ order: m, transition: ViewTransition.SlideLeft });
        this.orderHistoryController.onGoBack = () => {
            this.goBackToLastActiveRootView();
        };

        this.orderHistoryController.show(transition === undefined ? ViewTransition.SlideLeft : transition);
    }

    private showPaymentHistory(transition?: ViewTransition) {
        if (this.paymentHistoryController === undefined) {
            this.paymentHistoryController = new PaymentHistoryController();
            this.paymentHistoryController.stackTo(this.$mainContainer);
        }

        if (transition === undefined) {
            this.lastActiveRootView = this.getActiveViewController();
        }
        this.paymentHistoryController.onClick = (m) =>
            this.showPaymentReceipt({ payment: m, transition: ViewTransition.SlideLeft });
        this.paymentHistoryController.onGoBack = () => {
            this.goBackToLastActiveRootView(false);
        };

        this.paymentHistoryController.show(transition === undefined ? ViewTransition.SlideLeft : transition);
    }

    private async showReceipt(data: IReceiptControllerTransitionData) {
        if (this.receiptController === undefined) {
            this.receiptController = new ReceiptController();
            this.receiptController.stackTo(this.$mainContainer);
        }

        this.receiptController.onGoBack = () => {
            if (data.onGoBack) {
                data.onGoBack();
            } else {
                this.showOrderHistory(ViewTransition.SlideRight);
            }

            ReceiptController.onViewUnmounted.add(async () => {
                if (this.receiptController) {
                    await this.receiptController.dispose();
                    this.receiptController = undefined;
                }
            });
        };
        this.receiptController.show(data.transition, data);
    }

    private showPaymentReceipt(data: IPaymentReceiptControllerTransitionData) {
        if (this.paymentReceiptController === undefined) {
            this.paymentReceiptController = new PaymentReceiptController();
            this.paymentReceiptController.stackTo(this.$mainContainer);
        }

        this.paymentReceiptController.onGoBack = () => {
            if (data.onGoBack) {
                data.onGoBack();
            } else {
                this.showPaymentHistory(ViewTransition.SlideRight);
            }

            PaymentReceiptController.onViewUnmounted.add(async () => {
                if (this.paymentReceiptController) {
                    await this.paymentReceiptController.dispose();
                    this.paymentReceiptController = undefined;
                }
            });
        };

        this.paymentReceiptController.show(data.transition, data);
    }

    private showOpenBills(transition?: ViewTransition) {
        if (this.openBillsController === undefined) {
            this.openBillsController = new OpenBillsController();
            this.openBillsController.stackTo(this.$mainContainer);
        }

        if (transition === undefined) {
            this.lastActiveRootView = this.getActiveViewController();
        }

        this.openBillsController.onGoBack = () => {
            this.goBackToLastActiveRootView(false);
        };

        this.openBillsController.show(transition === undefined ? ViewTransition.SlideLeft : transition);
    }

    private showPaymentMethodsReport(transition?: ViewTransition) {
        if (this.paymentMethodReportController === undefined) {
            this.paymentMethodReportController = new PaymentMethodReportController();
            this.paymentMethodReportController.stackTo(this.$mainContainer);
        } else {
            this.paymentMethodReportController.triggerDataUpdate();
        }

        if (transition === undefined) {
            this.lastActiveRootView = this.getActiveViewController();
        }

        this.paymentMethodReportController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.paymentMethodReportController.show(transition === undefined ? ViewTransition.SlideLeft : transition);
    }

    private showHelp() {
        if (this.helpController === undefined) {
            this.helpController = new HelpController();
            this.helpController.stackTo(this.$mainContainer);

            this.helpController.onReportIssue = () => {
                if (this.reportIssueController === undefined) {
                    this.reportIssueController = new ReportIssueController();
                    this.reportIssueController.onGoBack = () => {
                        this.helpController!.show(ViewTransition.SlideRight);
                    };
                    this.reportIssueController.stackTo(this.$mainContainer);
                }

                this.reportIssueController.show(ViewTransition.SlideLeft);
            };

            this.helpController.onRate = () => {
                if (this.appRateController === undefined) {
                    this.appRateController = new AppRateController();
                    this.appRateController.onGoBack = () => {
                        this.helpController!.show(ViewTransition.SlideRight);
                    };

                    this.appRateController.stackTo(this.$mainContainer);
                }

                this.appRateController.show(ViewTransition.SlideLeft);
            };
        }

        this.lastActiveRootView = this.getActiveViewController();

        this.helpController.onGoBack = () => {
            this.goBackToLastActiveRootView();
        };

        this.helpController.show(ViewTransition.SlideLeft);
    }

    public showAllocation(transition?: ViewTransition) {
        this.allocationController!.show(transition);
    }

    public showServices(transition?: ViewTransition) {
        this.servicesController!.show(transition);
        this.servicesController!.onServiceClicked = (newService) => {
            if (this.onServiceClicked) {
                this.onServiceClicked(newService);
            }
        };
    }

    public showRequiredOrderService(orderService: IService, transition?: ViewTransition) {
        if (this.isPreorderService(orderService)) {
            this.showPreorder({ isMainNavigation: true }, transition);
        } else {
            this.showOrder({
                isMainNavigation: true,
                hasPreorderTimeStep: false,
                transition: transition,
            });
        }
    }

    public showRoomPlan(transition?: ViewTransition) {
        if (this.roomPlanController === undefined) {
            this.roomPlanController = new RoomPlanController();
            this.roomPlanController.stackTo(this.$mainContainer);
        }

        this.roomPlanController.onShowServices = () => this.showServices(ViewTransition.Fade);
        this.roomPlanController.show(transition);
    }

    private isPreorderService(orderService?: IService) {
        return orderService?.useCase != null && orderService.useCase.usePreorder === true;
    }

    private goBackToLastActiveRootView(fallbackToOrder: boolean = true) {
        if (this.lastActiveRootView !== undefined) {
            this.lastActiveRootView.show(ViewTransition.SlideRight);
            return;
        }

        if (fallbackToOrder) this.showOrder({ isMainNavigation: false });
    }

    public goToFirstViewController(transition?: ViewTransition) {
        if (TastierClient.context.currentAllocation === undefined) return;

        //check if only one service is active
        if (Helper.isSingleService()) {
            const defaultService = TastierClient.context.currentAllocation.services[0];
            TastierClient.context.updateSelectedService(defaultService.id);

            switch (defaultService.serviceType) {
                case ServiceType.Covid:
                    this.showCovid(transition);
                    return;
                case ServiceType.Documents:
                    this.showDocuments(transition);
                    return;
                case ServiceType.Offer:
                case ServiceType.Order:
                    TastierClient.resetPayContext();
                    this.showRequiredOrderService(defaultService, transition);
                    return;
                case ServiceType.Pay:
                    TastierClient.resetPayContext();
                    this.showPayOrder(transition);
                    return;
                case ServiceType.RoomPlan:
                    this.showRoomPlan(transition);
                    return;
            }
        }

        //if multi service is enabled, go to services controller
        this.showServices(transition);
    }

    private getActiveViewController(): BaseController | undefined {
        return BaseController.getActiveCtrl(this.$mainContainer);
    }

    public isDoneControllerVisible() {
        const activeController = this.getActiveViewController();
        return activeController instanceof DoneController && activeController.isVisible();
    }
}
